import {ReactNode, useContext, useEffect, useState} from "react";
import QRCodeStyleViewContext from "./QRCodeStyleViewContext";
import QRCodeStyle from "../../../../../api/qr-code/style/QRCodeStyle";
import {Grid} from "@mantine/core";
import Preview from "./preview/Preview";
import QRCodeStyleViewTypes from "./QRCodeStyleViewTypes";
import IndexView from "./views/index/IndexView";
import MoreIconsView from "./views/more/icon/MoreIconsView";
import UploadIconView from "./views/upload-icon/UploadIconView";
import QRCode from "../../../../../api/qr-code/QRCode";
import QRCodeContext from "../../../../../contexts/qr-code/QRCodeContext";
import Notifications from "../../../../../utils/notifications/Notifications";
import QRCodeStyleApi from "../../../../../api/qr-code/style/QRCodeStyleApi";
import QRCodeApi from "../../../../../api/qr-code/QRCodeApi";
import Payload from "./Payload";
import QRCodeHelper from "../../../../../helpers/qr-code/QRCodeHelper";
import QRCodeStyling, {DotType, Options} from "qr-code-styling";
import MoreCornersView from "./views/more/corners/MoreCornersView";
import DotsView from "./views/dots/DotsView";

type QRCodeStyleViewProps = {
    /**
     * QRCode's ID to edit.
     * Using ID to fetch QRCode again - avoid
     * @since 0.1.9
     * @author Kacper Faber
     */
    qrCodeId: number | undefined;
}

function QRCodeStyleView(props: QRCodeStyleViewProps) {
    /**
     * QRCodeContext
     * @since 0.1.9
     * @author Kacper Faber
     */
    const qrCtx = useContext(QRCodeContext);

    /**
     * QRCode from API [from context really]
     * @since 0.1.9
     * @author Kacper Faber
     */
    const [qrCode, setQRCode] = useState<QRCode>();

    /**
     * Original style
     * @author Kacper Faber
     * @since 0.1.9
     */
    const [style, setStyle] = useState<QRCodeStyle | null>();

    /**
     * Active view
     * @author Kacper Faber
     * @since 0.1.9
     */
    const [activeView, setActiveView] = useState<QRCodeStyleViewTypes>("index");

    /**
     * Payload to use
     * @author Kacper Faber
     * @since 0.1.9
     */
    const [payload, setPayload] = useState<Payload>();

    /**
     * Wraps your children inside a QRCodeStyleViewContext
     * @since 0.1.9
     * @author Kacper Faber
     */
    const wrapContext = (children: ReactNode) => {
        return (
            <QRCodeStyleViewContext.Provider
                value={{renderFile, render, payload, style, setPayload, setStyle, activeView, setActiveView, qrCode, setQRCode, save}}
                children={children}
            />
        );
    };

    /**
     * Renders the appropriate view according to 'activeView' state.
     * @author Kacper Faber
     * @since 0.1.9
     */
    const renderView = () => {
        if (activeView === 'index') {
            return (
                <IndexView/>
            );
        }
        else if (activeView === 'more-icons') {
            return (
                <MoreIconsView/>
            );
        }
        else if (activeView === 'upload-icon') {
            return (
                <UploadIconView/>
            );
        }
        else if (activeView === 'more-corners') {
            return (
                <MoreCornersView/>
            );
        }
        else if (activeView === 'more-dots') {
            return (
                <DotsView/>
            );
        }

        throw new Error(`Active view ${activeView} is not implemented.`);
    };

    /**
     * Reload the style when QRCode is changed.
     * @since 0.1.9
     * @author Kacper Faber
     */
    const reloadStyle = () => {
        setStyle(qrCode?.style);
    };

    /**
     * Reload the payload when QRCodeStyle is changed.
     * Creates empty payload, if style is empty.
     * @since 0.1.9
     * @author Kacper Faber
     */
    const reloadPayload = () => {
        if (!style) {
            setPayload({
                name: 'not-blank',
                icon: null
            });

            return;
        }

        setPayload(
            {
                name: style.name,
                icon: style.icon?.id,
                iconInstance: style.icon,
                dotsStyle: style.dotsStyle,
                dotsColor: style.dotsColor,
                cornersSquareColor: style.cornersSquareColor,
                cornersSquareStyle: style.cornersSquareStyle,
                cornersDotStyle: style.cornersDotStyle,
                width: style.width,
                iconMargin: style.iconMargin,
                iconSize: style.iconSize,
                cornersDotColor: style.cornersDotColor,
                margin: style.margin
            }
        );
    }

    const onReloadQRCodeFailure = () => {
        Notifications.onFetchError();
        throw new Error(`Could not find QRCode with ID: ${props.qrCodeId}`);
    }

    /**
     * Reload QR code when props.qrCodeId is changed
     * @since 0.1.9
     * @author Kacper Faber
     */
    const reloadQRCode = () => {
        if (props.qrCodeId === undefined) {
            setQRCode(undefined);
            return;
        }

        qrCtx.getById(props.qrCodeId)
            .then(setQRCode)
            .catch(onReloadQRCodeFailure);
    }

    /**
     * Render the QR code using current payload
     * @author Kacper Faber
     * @since 0.1.9
     */
    const render = (p: Partial<Options>) => {
        /**
         * Get cornersDotStyle from payload in QRCodeStyling notation.
         * @author Kacper Faber
         * @since 0.1.9
         */
        const dotStyle = () => {
            const ds = payload?.cornersDotStyle;
            return ds == 'none' || ds == null ? undefined : ds;
        };

        /**
         * Convert dotsStyle to QRCodeStyling notation
         * @author Kacper Faber
         * @since 0.1.9
         */
        const dotsType: () => DotType = () => {
            const d = payload?.dotsStyle;
            if (!d) return 'square';
            if (d == 'extra_rounded') return 'extra-rounded';
            else if (d == 'classy_rounded') return 'classy-rounded';
            return d as DotType;
        }

        /**
         * Get cornersSquareStyle from payload in QRCodeStyling notation
         * @author Kacper Faber
         * @since 0.1.9
         */
        const squareStyle = () => {
            const ss = payload?.cornersSquareStyle;
            if (ss == 'none' || ss == null) {
                return undefined;
            }
            if (ss == 'extra_rounded') {
                return 'extra-rounded'
            }
            return ss;
        }

        /**
         * Get dot color from payload in QRCodeStyling notation
         * @author Kacper Faber
         * @since 0.1.9
         */
        const dotColor: () => string | undefined = () => {
             return payload?.cornersDotColor ?? undefined
        }

        /**
         * Get square color from payload in QRCodeStyling notation.
         * @author Kacper Faber
         * @since 0.1.9
         */
        const squareColor: () => string | undefined = () => {
             const v = payload?.cornersSquareColor;
             if (!v) {
                 return undefined;
             }
             return v;
        }

        const dotsColor: () => string | undefined = () => {
            return payload?.dotsColor ?? undefined
        }

        return new QRCodeStyling({
            data: qrCode?.meta?.link ? qrCode?.meta.link : 'test',
            image: payload?.iconInstance?.image ?? undefined,
            width: 500,
            height: 500,
            imageOptions: {
                margin: payload?.iconMargin ?? 1,
                imageSize: payload?.iconSize ?? .5
            },
            cornersDotOptions: {
                type: dotStyle(),
                color: dotColor()
            },
            cornersSquareOptions: {
                type: squareStyle(),
                color: squareColor()
            },
            dotsOptions: {
                type: dotsType(),
                color: dotsColor()
            },
            ...p,
        });
    };

    /**
     * Render QR code from payload to file.
     * @author Kacper Faber
     * @since 0.1.9
     */
    const renderFile = async (options: Partial<Options>) => {
        const styling = render(options);
        const data = await styling.getRawData('png');
        return QRCodeHelper.toFile(data, qrCode ? `${qrCode.name}.png` : 'qr.png', 'png')!!;
    }

    /**
     * Updates or create new QRCodeStyle [using payload].
     * Should update the 'style', if success.
     * @since 0.1.9
     * @author Kacper Faber
     */
    const save = async () => {
        if (!payload) {
            throw new Error('No payload - cannot save.');
        }

        // TODO: QRCodeStyleView -> save() [Context implementation]
        //  Improve this shit

        if (style) {
            await QRCodeStyleApi.updateById(style.id, payload);

            if (qrCode) {
                const file = await renderFile({});

                setQRCode(
                    await QRCodeApi.editById(qrCode.id, { name: qrCode.name, style: style.id, image: file })
                );
            }

            setStyle(style);
            return style;
        }

        const createdStyle = await QRCodeStyleApi.create(payload);
        setStyle(createdStyle);

        if (qrCode) {
            const file = await renderFile({});

            setQRCode(
                await QRCodeApi.editById(qrCode.id,{ image: file, name: qrCode.name, style: createdStyle.id })
            )
        }

        return createdStyle;
    }

    /**
     * Reset active view if
     * @author Kacper Faber
     * @since 0.1.9
     */
    const resetActiveView = () => {
        setActiveView('index');
    }

    useEffect(reloadQRCode, [props.qrCodeId]);

    useEffect(resetActiveView, [props.qrCodeId]);

    // Reload the style when qrCode is changed.
    useEffect(reloadStyle, [qrCode]);

    useEffect(reloadPayload, [style]);

    return wrapContext(
        <Grid styles={{inner: {height: '100%'}, root: {height: '100%'}}}>
            <Grid.Col span={4}>
                <Preview/>
            </Grid.Col>

            <Grid.Col span={8}>
                {renderView()}
            </Grid.Col>
        </Grid>
    );
}

// noinspection JSUnusedGlobalSymbols
export default QRCodeStyleView;

// TODO: QRCodeStyleView.tsx
//  Implement the view.